import { BrowserModule, HammerModule } from '@angular/platform-browser';
import { ErrorHandler, NgModule } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClient, HttpClientModule } from '@angular/common/http';

import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';

import { LoggerModule } from 'ngx-logger';

import { NgxImgModule } from 'ngx-img';

import { ToastrModule } from 'ngx-toastr';

import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';

import { JWT_OPTIONS, JwtHelperService } from '@auth0/angular-jwt';

import { NgcCookieConsentConfig, NgcCookieConsentModule } from 'ngx-cookieconsent';

import * as fromAuthentication from '@app/modules/authentication/reducers/authentication.reducer';
import { AuthenticationEffects } from '@app/modules/authentication/effects/authentication.effects';
import { NotificationsEffects } from '@app/modules/notifications/effects/notifications.effects';

import { clearState } from '@app/modules/authentication/reducers/clear-state.reducer';

import { SharedModule } from '@app/shared/shared.module';

import { AppRoutingModule } from './app-routing.module';

import { AppComponent } from './app.component';

import { MockInterceptorConfig } from '@app/mock.config';

import { environment } from 'environments/environment';
import { SocialLoginModule } from 'angularx-social-login';
import { CalendarModule, DateAdapter } from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';

const cookieConfig: NgcCookieConsentConfig = {
  cookie: {
    domain: environment.cookie.domain
  },
  position: 'bottom',
  theme: 'classic',
  palette: {
    popup: {
      background: '#000000',
      text: '#ffffff',
      link: '#ffffff'
    },
    button: {
      background: '#44bd32',
      text: '#ffffff',
      border: 'transparent'
    }
  },
  type: 'info',
  content: {
    href: environment.cookie.cookiePolicyURL,
  }
};

// TODO: Uncomment when Sentry is set up for ArtOil.
// Sentry.init({
//   dsn: environment.sentry.dsn
// });

@NgModule({
  declarations: [
    AppComponent
  ],
  imports: [
    BrowserModule,
    HammerModule,
    BrowserAnimationsModule,
    HttpClientModule,
    StoreModule.forRoot({}, {
      metaReducers: [clearState],
      runtimeChecks: {
        strictActionImmutability: false,
      }
    }),
    EffectsModule.forRoot([NotificationsEffects]),
    StoreModule.forFeature(fromAuthentication.authenticationFeatureKey, fromAuthentication.reducer),
    EffectsModule.forFeature([AuthenticationEffects]),
    StoreDevtoolsModule.instrument(),
    LoggerModule.forRoot({level: environment.loggerLevel}),
    ToastrModule.forRoot(),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    MockInterceptorConfig.forRoot(),
    SharedModule.forRoot(),
    NgxImgModule.forRoot(),
    SocialLoginModule,
    NgcCookieConsentModule.forRoot(cookieConfig),
    AppRoutingModule,
    CalendarModule.forRoot({
      provide: DateAdapter,
      useFactory: adapterFactory,
    }),
  ],
  providers: [
    JwtHelperService,
    {provide: JWT_OPTIONS, useValue: {}},
    {
      provide: ErrorHandler,
      useValue: {},
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
}

// required for AOT compilation
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}
